import React, { useState, useEffect } from 'react';
import ConversationList from './ConversationList';
import Chat from './Chat';
import UserNotFoundMessage from './UserNotFoundMessage';
import chatAPI from '../../services/ChatAPI';
import { useUser } from '../../context/UserContext';
import './ChatContainer.css';

interface Conversation {
  chatId: string;
  otherUser: {
    id: string;
    firstName: string;
    lastName: string;
    imageUrl?: string;
  };
  lastMessage: {
    content: string;
    senderId: string;
    createdAt: string;
  };
  unreadCount: number;
  updatedAt: string;
}

interface MatchedUser {
  name: string;
  fullName: string;
  avatar?: string;
  birthCity?: string;
  email?: string;
  matchedUserId?: string;
}

interface ChatContainerProps {
  onClose: () => void;
  matchedUser?: MatchedUser; // Optional matched user to start chat with
}

const ChatContainer: React.FC<ChatContainerProps> = ({ onClose, matchedUser }) => {
  const { user } = useUser();
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [, setShowConversationList] = useState(!matchedUser);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [showUserNotFound, setShowUserNotFound] = useState(false);
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
  };

  const handleCloseChat = () => {
    setSelectedConversation(null);
  };

  const handleCloseContainer = () => {
    onClose();
  };

  const handleSendInvitation = async () => {
    if (!matchedUser || !user) return;
    
    try {
      setIsSendingInvitation(true);
      
      await chatAPI.sendInvitationEmail(matchedUser, user);
      
      // Show success message
      alert('Invitation sent successfully! They will receive an email to join iGiTREE and connect with you.');
      
      // Close the user not found message and go back to conversation list
      setShowUserNotFound(false);
      setShowConversationList(true);
      
    } catch (error) {
      console.error('Failed to send invitation:', error);
      alert('Failed to send invitation. Please try again or contact support.');
    } finally {
      setIsSendingInvitation(false);
    }
  };

  const handleCloseUserNotFound = () => {
    setShowUserNotFound(false);
    setShowConversationList(true);
  };

  // Handle matched user conversation
  useEffect(() => {
    if (matchedUser) {
      console.log('ChatContainer received matchedUser:', matchedUser);
      
      // TEMPORARY: Hard-code email for testing
      const matchedUserWithEmail = {
        ...matchedUser,
        email: matchedUser.email || 'bizip04@gmail.com'
      };
      
      handleMatchedUserChat(matchedUserWithEmail);
    }
    // Intentional: run when matchedUser changes; handler uses latest closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedUser]);

  const handleMatchedUserChat = async (matchedUser: MatchedUser) => {
    try {
      setIsLoadingConversation(true);      
      // Validate user data
      if (!matchedUser || (!matchedUser.matchedUserId && !matchedUser.fullName)) {
        console.error('Invalid matched user data:', matchedUser);
        throw new Error('Invalid user data provided');
      }
      
      // // Use name if available, otherwise use fullName as fallback
      const userId = matchedUser.matchedUserId;
      
      // // Log authenticated user information
      console.log('Current authenticated user ID:', user?.id);
      console.log('Current matched id .....:', userId);
      
      // Get or create conversation (ensures uniqueness and handles both cases)
      const response = await chatAPI.getOrCreateConversation(userId);
      const conversation = response.conversation;
      
      if (!conversation) {
        throw new Error('Failed to get or create conversation');
      }

      // // Create a conversation object for the chat component
      const nameParts = (matchedUser.fullName || 'Unknown User').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      console.log('Creating conversation object:', {
        matchedUser,
        nameParts,
        firstName,
        lastName
      });
      
      const conversationObj: Conversation = {
        chatId: conversation.id,
        otherUser: {
          id: userId || '',
          firstName: firstName,
          lastName: lastName,
          imageUrl: matchedUser.avatar
        },
        lastMessage: conversation.lastMessage || {
          content: '',
          senderId: '',
          createdAt: new Date().toISOString()
        },
        unreadCount: 0,
        updatedAt: conversation.updatedAt || new Date().toISOString()
      };

      setSelectedConversation(conversationObj);
      setShowConversationList(false);
    } catch (error: any) {
      console.error('Failed to start chat with matched user:', error);
      
      // Check if user doesn't exist
      if (error.message === 'USER_NOT_FOUND') {
        setShowUserNotFound(true);
        setShowConversationList(false);
      } else {
        // Show error message to user for other errors
        alert('Unable to start chat. Please try again or contact support if the issue persists.');
        
        // Fallback to conversation list
        setShowConversationList(true);
      }
    } finally {
      setIsLoadingConversation(false);
    }
  };

  return (
    <div className="chat-container-overlay">
      <div className="chat-container-wrapper">
        {/* User Not Found Message */}
        {showUserNotFound && matchedUser && (
          <UserNotFoundMessage
            matchedUser={{
              ...matchedUser,
              email: matchedUser.email || 'bizip04@gmail.com'
            }}
            onClose={handleCloseUserNotFound}
            onSendInvitation={handleSendInvitation}
            isSendingInvitation={isSendingInvitation}
          />
        )}
        {isLoadingConversation ? (
          <div className="chat-container">
            <div className="chat-container-header">
              <h4>Starting Chat...</h4>
              <button className="btn-close" onClick={handleCloseContainer}></button>
            </div>
            <div className="chat-loading-content">
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted">Setting up your conversation...</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="chat-container">
            <div className="chat-container-header">
              <h4>Messages</h4>
              <button className="btn-close" onClick={handleCloseContainer}></button>
            </div>
            <div className="chat-main-layout">
              {/* Conversation List - Always visible */}
              <ConversationList 
                onSelectConversation={handleSelectConversation}
              />
              
              {/* Right Panel - Shows welcome message or chat */}
              {selectedConversation ? (
                // Show chat messages when a conversation is selected
                <div className="chat-messages-panel">
                  <Chat
                    key={selectedConversation.chatId}
                    matchedUser={{
                      name: selectedConversation.otherUser.id,
                      fullName: `${selectedConversation.otherUser.firstName || ''} ${selectedConversation.otherUser.lastName || ''}`.trim() || 'Unknown User',
                      avatar: selectedConversation.otherUser.imageUrl || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRPoAJ0jJpb24AFIkHZ4RfZTXFAHLLjxm33Fg&s",
                      birthCity: '' // You might want to add this to the conversation data
                    }}
                    conversationId={selectedConversation.chatId}
                    onClose={handleCloseChat}
                  />
                </div>
              ) : (
                // Show welcome message when no conversation is selected
                <div className="chat-welcome-panel">
                  <div className="welcome-content">
                    <div className="welcome-icon">
                      <i className="fas fa-comments"></i>
                    </div>
                    <h5 className="welcome-title">Welcome to iGiTREE Chat</h5>
                    <p className="welcome-description">
                      Connect with your family members and start meaningful conversations. 
                      Select a conversation from the list to begin chatting.
                    </p>
                    <div className="welcome-features">
                      <div className="welcome-feature">
                        <i className="fas fa-shield-alt text-success"></i>
                        <span>Secure & Private</span>
                      </div>
                      <div className="welcome-feature">
                        <i className="fas fa-bolt text-warning"></i>
                        <span>Real-time Messaging</span>
                      </div>
                      <div className="welcome-feature">
                        <i className="fas fa-mobile-alt text-info"></i>
                        <span>Mobile Friendly</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatContainer;
